import { useEffect, useState } from "react";
import { FileText, Plus, Copy, Trash2, Star, Eye, Save, Loader2, X } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import RichContent from "../public/RichContent";
import { fieldsFor } from "../../config/contentSeo";

const ENTITY_TYPES = ["product", "category", "brand", "page", "blog", "guide", "local", "seasonal"];
const PLACEHOLDERS = ["{{Product Name}}", "{{Category Name}}", "{{Brand Name}}", "{{Website Name}}", "{{Primary Keyword}}", "{{Secondary Keywords}}"];
const fieldsForType = (t) => fieldsFor(["blog", "guide", "local", "seasonal"].includes(t) ? "page" : t);

const blank = (t = "product") => ({ name: "", entity_type: t, description: "", blocks: {}, faqs: [], is_default: false });

export default function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/seo/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name.trim()) { toast.error("Name required."); return; }
    setSaving(true);
    try {
      if (editing.template_id) await api.put(`/admin/seo/templates/${editing.template_id}`, editing);
      else await api.post("/admin/seo/templates", editing);
      toast.success("Template saved."); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const duplicate = async (t) => { await api.post(`/admin/seo/templates/${t.template_id}/duplicate`); toast.success("Duplicated."); load(); };
  const remove = async (t) => { await api.delete(`/admin/seo/templates/${t.template_id}`); toast.success("Deleted."); load(); };

  const setBlock = (key, val) => setEditing((e) => ({ ...e, blocks: { ...e.blocks, [key]: val } }));
  const setFaq = (i, patch) => setEditing((e) => ({ ...e, faqs: e.faqs.map((f, j) => j === i ? { ...f, ...patch } : f) }));

  return (
    <div className="space-y-4" data-testid="template-manager">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-cinzel text-white flex items-center gap-2"><FileText className="h-5 w-5 text-gold" /> Content Templates</h2>
          <p className="text-xs text-white/50 mt-1">Reusable content blocks with placeholders. Applying a template never overwrites existing content without confirmation, and never invents data — missing placeholders render blank.</p>
        </div>
        <Button className="cf-gold-btn gap-1" onClick={() => setEditing(blank())} data-testid="template-new"><Plus className="h-4 w-4" /> New Template</Button>
      </div>

      {loading ? <div className="p-6 text-center text-white/40 text-sm flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div> :
        templates.length === 0 ? <p className="text-white/40 text-sm py-6 text-center" data-testid="template-empty">No templates yet. Create one to speed up content authoring.</p> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {templates.map((t) => (
              <div key={t.template_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`template-${t.template_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white/90 font-medium truncate">{t.name}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge variant="outline" className="text-[10px] border-white/20 text-white/60">{t.entity_type}</Badge>
                      {t.is_default && <Badge variant="outline" className="text-[10px] border-gold/40 text-gold gap-0.5"><Star className="h-2.5 w-2.5" />default</Badge>}
                      <span className="text-[10px] text-white/35">{Object.keys(t.blocks || {}).length} block(s)</span>
                    </div>
                  </div>
                </div>
                {t.description && <p className="text-xs text-white/45 mt-2 line-clamp-2">{t.description}</p>}
                <div className="flex items-center gap-1 mt-3">
                  <Button variant="outline" size="sm" className="h-7 gap-1" onClick={() => setEditing({ ...t, faqs: t.faqs || [], blocks: t.blocks || {} })} data-testid={`template-edit-${t.template_id}`}>Edit</Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50" title="Preview" onClick={() => setPreview(t)} data-testid={`template-preview-${t.template_id}`}><Eye className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50" title="Duplicate" onClick={() => duplicate(t)}><Copy className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" title="Delete" onClick={() => remove(t)} data-testid={`template-delete-${t.template_id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>}

      {/* Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="template-editor">
          <DialogHeader>
            <DialogTitle>{editing?.template_id ? "Edit" : "New"} Template</DialogTitle>
            <DialogDescription>Use placeholders anywhere in your blocks. Available: {PLACEHOLDERS.join(", ")}.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Name</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-black/30 border-white/15" data-testid="template-name-input" /></div>
                <div><Label className="text-xs">Applies to</Label>
                  <select value={editing.entity_type} onChange={(e) => setEditing({ ...editing, entity_type: e.target.value, blocks: {} })} className="cf-select w-full bg-black/30 border border-white/15 rounded-md px-3 py-2 text-sm" data-testid="template-entity-type">
                    {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div><Label className="text-xs">Description</Label><Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-black/30 border-white/15" /></div>
              <label className="flex items-center gap-2 text-sm"><Switch checked={editing.is_default} onCheckedChange={(v) => setEditing({ ...editing, is_default: v })} data-testid="template-default-toggle" /> Set as default template for {editing.entity_type}</label>

              <div className="space-y-3">
                {fieldsForType(editing.entity_type).map((f) => (
                  <div key={f.key}>
                    <Label className="text-xs text-white/70">{f.label}</Label>
                    <Textarea rows={4} value={editing.blocks[f.key] || ""} onChange={(e) => setBlock(f.key, e.target.value)} placeholder={`HTML with placeholders, e.g. <p>About {{Product Name}} from {{Website Name}}.</p>`} className="bg-black/30 border-white/15 font-mono text-xs" data-testid={`template-block-${f.key}`} />
                  </div>
                ))}
              </div>

              <div>
                <div className="flex items-center justify-between"><Label className="text-xs text-white/70">FAQs</Label>
                  <Button type="button" variant="outline" size="sm" className="h-7 gap-1" onClick={() => setEditing({ ...editing, faqs: [...editing.faqs, { question: "", answer: "" }] })}><Plus className="h-3.5 w-3.5" /> Add FAQ</Button></div>
                {editing.faqs.map((f, i) => (
                  <div key={i} className="rounded-md border border-white/10 p-2 mt-2 space-y-1">
                    <div className="flex gap-1"><Input value={f.question} onChange={(e) => setFaq(i, { question: e.target.value })} placeholder="Question (placeholders ok)" className="bg-black/30 border-white/15 text-sm" /><Button type="button" variant="ghost" size="icon" className="text-red-400" onClick={() => setEditing({ ...editing, faqs: editing.faqs.filter((_, j) => j !== i) })}><X className="h-4 w-4" /></Button></div>
                    <Textarea rows={2} value={f.answer} onChange={(e) => setFaq(i, { answer: e.target.value })} placeholder="Answer HTML" className="bg-black/30 border-white/15 font-mono text-xs" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="cf-gold-btn gap-1" onClick={save} disabled={saving} data-testid="template-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.name}</DialogTitle><DialogDescription>Template preview (placeholders shown literally). Applying to a real item substitutes them.</DialogDescription></DialogHeader>
          {preview && (
            <div className="space-y-4">
              {fieldsForType(preview.entity_type).filter((f) => preview.blocks?.[f.key]).map((f) => (
                <div key={f.key}><p className="text-xs uppercase tracking-wider text-white/40 mb-1">{f.label}</p><div className="rounded-lg border border-white/10 bg-black/20 p-3"><RichContent html={preview.blocks[f.key]} /></div></div>
              ))}
              {(preview.faqs || []).length > 0 && <div><p className="text-xs uppercase tracking-wider text-white/40 mb-1">FAQs</p>{preview.faqs.map((f, i) => <div key={i} className="mb-2"><p className="text-sm text-white/85">{f.question}</p><RichContent html={f.answer} className="text-sm" /></div>)}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
